import React, { useEffect, useState } from "react";
import { Bell, BellRing, Loader2, Tag, Users, Wallet, CalendarClock } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Switch } from "./ui/switch";
import { Button } from "./ui/button";
import { useGuest } from "../contexts/GuestContext";
import {
  GUEST_ALERT_TYPES, getGuestAlertPrefs, saveGuestAlertPrefs, guestHasAnyAlert,
  enableGuestPush, getGuestPushSubscription, syncGuestAlertProfile, fetchGuestNotifications,
} from "../lib/guestAlerts";
import { DaySelect } from "./AlertTypeSettings";

const ICONS = { deal_alerts: Tag, community_deals: Users, budget_deal_alerts: Wallet, budget_reminder: CalendarClock };

const REASONS = {
  unsupported: "This browser can't show notifications. On iPhone, add Foodz Lizts to your Home Screen first.",
  denied: "Notifications are blocked. Allow them for this site in your browser settings, then try again.",
  server: "Couldn't turn on alerts right now. Please try again.",
};

export default function GuestAlertsPanel() {
  const { promptSignup } = useGuest();
  const [prefs, setPrefs] = useState(getGuestAlertPrefs());
  const [pushOn, setPushOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    getGuestPushSubscription().then((s) => setPushOn(!!s));
    fetchGuestNotifications().then(setRecent).catch(() => {});
  }, []);

  const update = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    saveGuestAlertPrefs(next);
    setMsg("");
    if (guestHasAnyAlert(next) && !pushOn) {
      setBusy(true);
      const res = await enableGuestPush().catch(() => ({ ok: false, reason: "server" }));
      setBusy(false);
      setPushOn(res.ok);
      if (!res.ok) {
        // Guests are push-only: without permission the alert can't be active
        const reverted = { ...prefs, ...Object.fromEntries(Object.keys(patch).filter((k) => k !== "budget_reminder_day").map((k) => [k, false])) };
        setPrefs(reverted);
        saveGuestAlertPrefs(reverted);
        setMsg(REASONS[res.reason] || REASONS.server);
      }
    } else {
      syncGuestAlertProfile().catch(() => {});
    }
  };

  return (
    <div className="space-y-4" data-testid="guest-alerts-panel">
      <Card className="border-green-200 bg-green-50">
        <CardContent className="p-4 flex items-start gap-3">
          {pushOn ? <BellRing className="h-5 w-5 text-green-700 mt-0.5" /> : <Bell className="h-5 w-5 text-green-700 mt-0.5" />}
          <div className="text-sm">
            <p className="font-semibold text-gray-900">Shopping alerts on this device</p>
            <p className="text-xs text-gray-600" data-testid="guest-push-status">
              {pushOn ? "Push notifications are on for this browser." : "Turn on any alert below — we'll ask your browser for permission."}
            </p>
          </div>
        </CardContent>
      </Card>

      {msg && <p className="text-xs text-red-600" data-testid="guest-alerts-error">{msg}</p>}

      <Card>
        <CardContent className="p-0 divide-y">
          {GUEST_ALERT_TYPES.map((t) => {
            const Icon = ICONS[t.key];
            return (
              <div key={t.key} className="p-4" data-testid={`guest-setting-${t.key}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center"><Icon className="h-4 w-4 text-gray-700" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">{t.title}</p>
                    <p className="text-xs text-gray-500">{t.desc}</p>
                  </div>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin text-gray-400" /> : (
                    <Switch checked={!!prefs[t.key]} onCheckedChange={(v) => update({ [t.key]: v })} data-testid={`guest-toggle-${t.key}`} />
                  )}
                </div>
                {t.key === "budget_reminder" && prefs.budget_reminder && (
                  <DaySelect value={prefs.budget_reminder_day} onChange={(d) => update({ budget_reminder_day: d })} testId="guest-budget-day" />
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-500">
        Alerts use your saved ZIP, radius, grocery list and budget from this device. Meal plan alerts and email alerts need an account.
      </p>
      <Button className="w-full bg-gray-900 text-white hover:bg-gray-800" onClick={() => promptSignup({ headline: "Get meal ideas & email alerts", subhead: "Start your 14-day free trial. No card, cancel anytime." })} data-testid="guest-alerts-trial-cta">
        Start free trial for meal alerts
      </Button>

      {recent.length > 0 && (
        <div className="space-y-2" data-testid="guest-recent-alerts">
          <p className="text-xs font-semibold text-gray-700">Recent alerts</p>
          {recent.slice(0, 10).map((n) => (
            <div key={n.id} className="rounded-lg border p-3 bg-white">
              <p className="text-sm font-medium text-gray-900">{n.title}</p>
              <p className="text-xs text-gray-600 whitespace-pre-line">{n.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
